import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, CheckCircle2, History, KeyRound, Plug, Radio, Webhook } from 'lucide-react';
import { getChannelCatalog, listChannexAdapters, recoverChannexBookings, registerChannexWebhook } from '@/lib/api/admin';
import { describeError } from '@/lib/api/client';
import type { ChannelAdapter, OtaChannel, RecoverBookingsResult } from '@/lib/api/types';
import { formatDateTime, formatNumber, plural } from '@/lib/format';
import { useDebounced, useUrlParam } from '@/lib/hooks';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, Prop } from '@/components/ui/card';
import { DataTable, type Column } from '@/components/ui/data-table';
import { Dialog } from '@/components/ui/dialog';
import { Notice, QueryError, Skeleton } from '@/components/ui/feedback';
import { FilterSelect, Input } from '@/components/ui/input';
import { PageHeader } from '@/components/ui/page';
import { TableToolbar } from '@/components/ui/toolbar';
import { toast } from '@/components/ui/toast';

/**
 * The Channex account itself — the two levers the backend reserves for a
 * super admin, and the catalogue of what the account can connect.
 *
 *  - The webhook is ONE global subscription for every property on the
 *    account, including ones added later; that is why it is not a hotel
 *    owner's button.
 *  - Recovery re-reads bookings created since a moment, for outages longer
 *    than the ~30 minutes the revision feed re-serves an unacknowledged
 *    booking. Deliberately manual: run on a schedule it would re-read the
 *    whole account forever.
 */
export function ChannexIntegrationPage() {
  const qc = useQueryClient();
  const catalog = useQuery({ queryKey: ['channel-catalog'], queryFn: getChannelCatalog, staleTime: 5 * 60_000 });
  const adapters = useQuery({ queryKey: ['channex-adapters'], queryFn: listChannexAdapters, staleTime: 5 * 60_000, retry: false });
  const channex = catalog.data?.providers.find((p) => p.id === 'channex') ?? null;

  const subscribe = useMutation({
    mutationFn: registerChannexWebhook,
    onSuccess: (r) => { toast('success', 'Вебхук зарегистрирован', r.callbackUrl); qc.invalidateQueries({ queryKey: ['channel-catalog'] }); },
    onError: (err) => toast('error', 'Не удалось зарегистрировать', describeError(err)),
  });

  const [recoverOpen, setRecoverOpen] = useState(false);
  const [result, setResult] = useState<RecoverBookingsResult | null>(null);

  return (
    <>
      <PageHeader title="Интеграция Channex" subtitle="Аккаунт channel manager, общий для всех организаций: вебхук, восстановление броней и каталог того, что можно подключить." />

      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader icon={<Webhook size={18} />} title="Вебхук" subtitle="Одна подписка на весь аккаунт — так Channex сообщает о новых бронях мгновенно." />
          {catalog.isPending ? <Skeleton className="h-28" /> : catalog.isError ? <QueryError error={catalog.error} onRetry={() => catalog.refetch()} /> : !channex ? (
            <Notice tone="warn" title="Провайдер channex не значится в каталоге бэкенда" />
          ) : (
            <>
              <dl className="mb-4 divide-y divide-line">
                <Prop label="Доступы к Channex"><Badge tone={channex.configured ? 'ok' : 'warn'} dot>{channex.configured ? 'настроены' : 'нет'}</Badge></Prop>
                <Prop label="Секрет и публичный URL"><Badge tone={channex.webhookReady ? 'ok' : 'warn'} dot>{channex.webhookReady ? 'заданы' : 'не заданы'}</Badge></Prop>
                <Prop label="Подписка">
                  {channex.webhookSubscribed === null ? <Badge>неизвестно</Badge> : <Badge tone={channex.webhookSubscribed ? 'ok' : 'warn'} dot>{channex.webhookSubscribed ? 'активна' : 'нет'}</Badge>}
                </Prop>
              </dl>
              {!channex.webhookReady && <Notice tone="warn" className="mb-4">Регистрация ответит 400, пока в .env бэкенда не заданы CHANNEX_WEBHOOK_SECRET и API_PUBLIC_URL.</Notice>}
              <div className="flex justify-end">
                <Button icon={<Plug size={14} />} loading={subscribe.isPending} disabled={!channex.configured} onClick={() => subscribe.mutate()}>
                  {channex.webhookSubscribed ? 'Перерегистрировать вебхук' : 'Зарегистрировать вебхук'}
                </Button>
              </div>
              {subscribe.data && <p className="mt-3 break-all font-mono text-[11px] text-faint">{subscribe.data.callbackUrl}{subscribe.data.id ? ` · ${subscribe.data.id}` : ''}</p>}
            </>
          )}
        </Card>

        <Card>
          <CardHeader icon={<History size={18} />} title="Восстановление броней" subtitle="После сбоя дольше 30 минут: дочитать брони, созданные с указанного момента, из долговременного списка Channex." />
          <Notice tone="neutral" className="mb-4">Дедуплицируется с тем, что уже есть в PMS. Запускается вручную и по необходимости — по расписанию перечитывало бы весь аккаунт бесконечно.</Notice>
          <div className="flex justify-end"><Button variant="outline" icon={<History size={14} />} onClick={() => setRecoverOpen(true)}>Восстановить брони…</Button></div>
          {result && <RecoverResult result={result} />}
        </Card>

        <AdaptersCard adapters={adapters.data} loading={adapters.isPending} error={adapters.error} onRetry={() => adapters.refetch()} />

        <Card>
          <CardHeader icon={<Radio size={18} />} title="OTA в каталоге" subtitle="Каналы, для которых бэкенд знает инструкции по iCal-подключению." />
          {catalog.isPending ? <Skeleton className="h-40" /> : catalog.isError ? null : <OtaList channels={catalog.data.channels} />}
        </Card>
      </div>

      {recoverOpen && <RecoverDialog onClose={() => setRecoverOpen(false)} onDone={(r) => { setResult(r); setRecoverOpen(false); }} />}
    </>
  );
}

function RecoverDialog({ onClose, onDone }: { onClose: () => void; onDone: (r: RecoverBookingsResult) => void }) {
  // "Now" is read once, when the dialog opens: the max of the picker and the validity check agree.
  const [openedAt] = useState(() => Date.now());
  const [since, setSince] = useState(() => new Date(openedAt - 24 * 3600e3).toISOString().slice(0, 16));
  const run = useMutation({
    mutationFn: () => recoverChannexBookings(new Date(since).toISOString()),
    onSuccess: (r) => { toast(r.failed ? 'error' : 'success', 'Восстановление завершено', `создано ${r.created}, обновлено ${r.updated}, не удалось ${r.failed}`); onDone(r); },
    onError: (err) => toast('error', 'Восстановление не удалось', describeError(err)),
  });
  const valid = !Number.isNaN(new Date(since).getTime()) && new Date(since).getTime() < openedAt;
  return (
    <Dialog open onClose={onClose} title="Восстановить брони из Channex" description="Будут дочитаны брони, созданные с этого момента, по всем подключённым объектам."
      footer={<><Button variant="outline" onClick={onClose}>Отмена</Button><Button loading={run.isPending} disabled={!valid} icon={<History size={14} />} onClick={() => run.mutate()}>Запустить</Button></>}>
      <div className="space-y-4">
        <Input label="Созданные с" type="datetime-local" value={since} onChange={(e) => setSince(e.target.value)} max={new Date(openedAt).toISOString().slice(0, 16)} hint="Локальное время этого браузера; на бэкенд уйдёт в UTC." />
        <Notice tone="warn" icon={<AlertTriangle size={16} />}>Это боевой аккаунт Channex: восстановленные брони появятся у отелей сразу.</Notice>
      </div>
    </Dialog>
  );
}

function RecoverResult({ result }: { result: RecoverBookingsResult }) {
  const rows: Array<[string, number]> = [['Подключений', result.connections], ['Просмотрено', result.seen], ['Создано', result.created], ['Обновлено', result.updated], ['Отменено', result.cancelled], ['Пропущено', result.skipped], ['Отброшено', result.dropped], ['Не удалось', result.failed]];
  return (
    <div className="mt-4 border-t border-line pt-4">
      <p className="mb-2 flex items-center gap-2 text-xs font-semibold text-muted"><CheckCircle2 size={13} className="text-ok" />Результат · {formatDateTime(new Date().toISOString())}</p>
      <dl className="grid grid-cols-2 gap-x-6 gap-y-1 text-sm sm:grid-cols-4">
        {rows.map(([l, v]) => <div key={l}><dt className="text-[11px] text-faint">{l}</dt><dd className={`font-semibold tabular-nums ${l === 'Не удалось' && v > 0 ? 'text-danger' : 'text-ink'}`}>{formatNumber(v)}</dd></div>)}
      </dl>
      {result.failures.length > 0 && <ul className="mt-3 space-y-1 text-xs text-danger">{result.failures.map((f, i) => <li key={i}>{f}</li>)}</ul>}
    </div>
  );
}

function AdaptersCard({ adapters, loading, error, onRetry }: { adapters?: ChannelAdapter[]; loading: boolean; error: unknown; onRetry: () => void }) {
  const [q, setQ] = useUrlParam('aq');
  const [kind, setKind] = useUrlParam('akind', 'all');
  const query = useDebounced(q.trim().toLowerCase(), 150);
  const kinds = useMemo(() => [...new Set((adapters ?? []).map((a) => a.kind).filter((k): k is string => !!k))].sort(), [adapters]);
  const filtered = useMemo(() => {
    let rows = adapters ?? [];
    if (query) rows = rows.filter((a) => `${a.code} ${a.title}`.toLowerCase().includes(query));
    if (kind !== 'all') rows = rows.filter((a) => a.kind === kind);
    return rows;
  }, [adapters, query, kind]);

  const columns: Column<ChannelAdapter>[] = [
    { key: 'title', header: 'OTA', sortValue: (a) => a.title, render: (a) => <><div className="font-semibold text-ink">{a.title}</div><div className="font-mono text-[11px] text-muted">{a.code}</div></> },
    { key: 'kind', header: 'Тип', sortValue: (a) => a.kind, render: (a) => a.kind ? <Badge>{a.kind}</Badge> : <span className="text-faint">—</span> },
    { key: 'mapping', header: 'Маппинг', sortValue: (a) => a.mappingMode, hideBelow: 'md', render: (a) => <span className="text-xs text-muted">{[a.mappingMode, a.propertyMapping].filter(Boolean).join(' · ') || '—'}</span> },
    { key: 'params', header: 'Полей', align: 'right', sortValue: (a) => a.params.length, hideBelow: 'md', render: (a) => `${a.params.filter((p) => p.type !== 'hidden').length}${a.rateParams.length ? ` + ${a.rateParams.length}` : ''}` },
    { key: 'messages', header: 'Чат с гостями', align: 'center', sortValue: (a) => a.messageSupport, render: (a) => a.messageSupport ? <Badge tone="ok" title="OTA передаёт переписку с гостями через Channex">есть</Badge> : <Badge tone="neutral" title="Переписка с гостями через Channex не поддерживается">нет</Badge> },
  ];

  return (
    <Card className="lg:col-span-2" padded={false}>
      <div className="p-5 pb-0"><CardHeader icon={<KeyRound size={18} />} title="Адаптеры Channex" subtitle="Справочник: каждая OTA, которую аккаунт может подключить, и что для этого нужно заполнить. Это не статус подключений — они у каждого отеля во вкладке «Каналы»." /></div>
      <div className="px-5 pb-5">
        {error ? <QueryError error={error} onRetry={onRetry} /> : (
          <>
            <TableToolbar search={{ value: q, onChange: setQ, placeholder: 'Название или код' }} shown={filtered.length} total={adapters?.length} onReset={q || kind !== 'all' ? () => { setQ(''); setKind('all'); } : null}>
              {kinds.length > 0 && <FilterSelect ariaLabel="Тип" value={kind} onChange={setKind} options={[{ value: 'all', label: 'Все типы' }, ...kinds.map((k) => ({ value: k, label: k }))]} />}
            </TableToolbar>
            <DataTable id="a_" rows={filtered} columns={columns} rowKey={(a) => a.code} defaultSort={{ key: 'title', dir: 'asc' }} loading={loading} empty={{ icon: <KeyRound size={28} />, title: adapters?.length ? 'Ничего не найдено' : 'Бэкенд не отдал список адаптеров' }} />
          </>
        )}
      </div>
    </Card>
  );
}

function OtaList({ channels }: { channels: OtaChannel[] }) {
  if (channels.length === 0) return <p className="text-xs text-muted">Каталог пуст.</p>;
  return (
    <ul className="divide-y divide-line">
      {channels.map((c) => (
        <li key={c.channel} className="flex items-center gap-3 py-2">
          <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: c.color }} />
          <div className="min-w-0 flex-1"><p className="text-sm font-semibold text-ink">{c.name}</p><p className="line-clamp-1 text-xs text-muted">{c.description}</p></div>
          <Badge mono>{c.channel}</Badge>
          <span className="text-xs text-faint">{plural(c.icalSteps.length, ['шаг', 'шага', 'шагов'])}</span>
        </li>
      ))}
    </ul>
  );
}
